import React from "react";
import { motion } from "framer-motion";
import {
  GraduationCap,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Info,
} from "lucide-react";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";

const WHATSAPP_DOCENTE =
  "https://wa.me/393773660476?text=" +
  encodeURIComponent(
    "Ciao Michele, sono un docente e vorrei acquistare \"Metodo di Mare\" con la Carta del Docente. Ho già generato il buono."
  );
const WHATSAPP_18APP =
  "https://wa.me/393773660476?text=" +
  encodeURIComponent(
    "Ciao Michele, ho 18 anni e vorrei acquistare \"Metodo di Mare\" con la Carta Cultura Giovani (18app). Ho già generato il buono."
  );
const EMAIL_MAILTO =
  "mailto:metododimare@gmail.com?subject=" +
  encodeURIComponent("Acquisto con Carta del Docente / 18app") +
  "&body=" +
  encodeURIComponent(
    "Ciao Michele,\nvorrei acquistare Metodo di Mare con:\n[ ] Carta del Docente\n[ ] Carta Cultura Giovani (18app)\n\nCodice buono: ____________________\nIndirizzo di spedizione: ____________________\n\nGrazie!"
  );

const stepsDocente = [
  {
    n: "01",
    title: "Genera il buono",
    text: "Vai su cartadeldocente.istruzione.it, sezione \"Crea buono\". Tipologia: Fisico → Libri e testi (anche digitali).",
  },
  {
    n: "02",
    title: "Indica l'importo",
    text: "€ 19,90 per il Paperback. Il sistema ti genera un codice univoco da inviarmi.",
  },
  {
    n: "03",
    title: "Inviami il codice",
    text: "Mandami il codice del buono via WhatsApp o email insieme al tuo indirizzo di spedizione.",
  },
  {
    n: "04",
    title: "Validazione e spedizione",
    text: "Valido il buono sul portale MIUR e ricevi il libro a casa entro 3-5 giorni lavorativi.",
  },
];

export default function CartaDocente() {
  return (
    <section
      id="carta-docente"
      data-testid="carta-docente-section"
      className="py-24 md:py-32 border-t border-[#E5E1DA] bg-gradient-to-br from-[#F4E8D6]/60 to-[#D6E5EC]/40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          {/* Left: Header */}
          <div className="md:col-span-5">
            <p className="overline text-[#0E7C8F]">
              Bonus governativi accettati
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05] text-[#0B192C]">
              Per docenti
              <br />
              <em className="text-[#0E7C8F]">e under 19.</em>
            </h2>
            <p className="mt-6 text-lg font-light text-[#3A4F6B] leading-relaxed max-w-md">
              Accetto la <strong>Carta del Docente</strong> e la{" "}
              <strong>Carta Cultura Giovani (18app)</strong> per l'acquisto
              diretto del libro Paperback (€19,90). Spedizione inclusa in
              tutta Italia.
            </p>

            <div className="mt-8 space-y-3">
              <div className="flex items-start gap-3 p-4 bg-[#FFFFFF] border-l-4 border-[#E89B6C]">
                <GraduationCap
                  size={22}
                  strokeWidth={1.3}
                  className="text-[#E89B6C] flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="font-serif-display text-lg leading-tight text-[#0B192C]">
                    Carta del Docente
                  </p>
                  <p className="text-sm font-light text-[#3A4F6B] mt-1">
                    Per docenti di ruolo · €500/anno · Categoria{" "}
                    <em>Libri e testi</em>
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#FFFFFF] border-l-4 border-[#0E7C8F]">
                <Sparkles
                  size={22}
                  strokeWidth={1.3}
                  className="text-[#0E7C8F] flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="font-serif-display text-lg leading-tight text-[#0B192C]">
                    Carta Cultura Giovani (18app)
                  </p>
                  <p className="text-sm font-light text-[#3A4F6B] mt-1">
                    Per neo-18enni · €500 una tantum · Categoria{" "}
                    <em>Libri</em>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Steps + CTA */}
          <div className="md:col-span-7">
            <p className="overline mb-6">Come procedere</p>
            <div className="grid grid-cols-2 gap-px bg-[#E5E1DA] border border-[#E5E1DA]">
              {stepsDocente.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-[#FFFFFF] p-6"
                  data-testid={`carta-docente-step-${i}`}
                >
                  <p className="font-serif-display text-3xl text-[#E89B6C]">
                    {s.n}
                  </p>
                  <h3 className="font-serif-display text-lg mt-2 leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm font-light text-[#3A4F6B] leading-relaxed">
                    {s.text}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={WHATSAPP_DOCENTE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="carta-docente-wa-btn"
                className="group flex-1 inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.18em] hover:bg-[#1E3557] transition-all"
              >
                <WhatsAppIcon size={15} className="text-[#25D366]" />
                Sono docente · Acquista
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href={WHATSAPP_18APP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="carta-18app-wa-btn"
                className="group flex-1 inline-flex items-center justify-center gap-3 px-6 py-4 border border-[#0B192C] text-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-all"
              >
                <WhatsAppIcon size={15} className="text-[#25D366] group-hover:text-[#F9F8F6]" />
                Ho 18 anni · Uso 18app
              </a>
            </div>
            <p className="mt-3 text-xs text-[#3A4F6B] font-light">
              In alternativa{" "}
              <a
                href={EMAIL_MAILTO}
                className="underline underline-offset-2 hover:text-[#0B192C]"
                data-testid="carta-docente-email-link"
              >
                scrivimi via email
              </a>{" "}
              allegando il codice del buono.
            </p>

            <div className="mt-8 p-5 bg-[#F9F8F6] border border-[#E5E1DA]">
              <div className="flex items-start gap-3">
                <Info
                  size={18}
                  strokeWidth={1.4}
                  className="text-[#0E7C8F] flex-shrink-0 mt-0.5"
                />
                <div className="text-sm font-light text-[#3A4F6B] leading-relaxed">
                  <p className="font-medium text-[#0B192C] mb-2">
                    Cosa ricevi:
                  </p>
                  <ul className="space-y-1.5">
                    <li className="flex items-start gap-2">
                      <CheckCircle2
                        size={14}
                        className="text-[#0E7C8F] flex-shrink-0 mt-0.5"
                      />
                      Libro Paperback firmato dall'autore
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2
                        size={14}
                        className="text-[#0E7C8F] flex-shrink-0 mt-0.5"
                      />
                      Spedizione tracciata inclusa in tutta Italia
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2
                        size={14}
                        className="text-[#0E7C8F] flex-shrink-0 mt-0.5"
                      />
                      Ricevuta valida ai fini della rendicontazione MIUR
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
